/* eslint-disable no-unused-vars */
import { enums, ILimitOrder, ITicker } from '@kattana-io/common';
import { connect, useDispatch } from 'react-redux';
import BigNumber from 'bignumber.js';
import DesktopTable from '../../../molecules/Table/DesktopTable';
import Badge from '../../../molecules/Badge';
import TokenInfo from '../../../molecules/TokenInfo';
import Text from '../../../atoms/Text';
import Actions from './Actions';
import { useTableData } from './useTableData';
import { actionShowModal } from '../../../actions/modal';
import { ModalTypes } from '../../../reducers/modal';
import { IStore } from '../../../reducers';
import { IAuthState } from '../../../reducers/auth';
import TickerCell from '../TickerCell';
import { decorateLimitOrderStatus } from '../../../helpers/decorators';

export const LS_LIMIT_ORDERS_HIDDEN_COLUMNS_KEY =
  'kattana-simple.limit_orders_table_columns_hidden';
export const LS_LIMIT_ORDERS_KEY = 'kattana-simple.limit_orders_table_columns';

interface ILOTableProps {
  orders: ILimitOrder[];
  names: Record<string, string>;
  tickers: Record<string, ITicker | undefined>;
  filter: string;
  loading: boolean;
  onFilter: (input: string) => void;
  width: number;
  auth: IAuthState;
}

/**
 * Return humanized condition of limit order price
 * @param param0
 * @returns
 */
const priceDisplayFormatter = ({ gt, price, price_usd }: ILimitOrder): string =>
  price_usd ? `${formatGt(gt)} ${price_usd} $` : `${formatGt(gt)} ${price}`;

/**
 * If gt is positive than price should be more than input
 * Otherwise - less
 * @param gt - flag
 * @returns
 */
const formatGt = (gt: boolean): string => (gt ? '>' : '< ');

const statusToBadgeKlass = (status: string): any => {
  switch (status) {
  case enums.LimitOrderStatus.WATCH:
  case enums.LimitOrderStatus.FIRED:
    return 'green';
  case enums.LimitOrderStatus.FAILED:
    return 'red';
  default:
    return 'grey';
  }
};

const getClassNameForRow = (row: any) => {
  if (row.status === 'FIRED') {
    return 'row--Buy text--green-600';
  }
  return 'row--Both text--grey-600';
};

/**
 * Block variable will trigger rerender on each block of this net
 * @param param0
 * @returns
 */
const LimitOrdersTable = ({
  tickers,
  filter,
  loading,
  names,
  onFilter,
  orders,
  width,
  auth,
}: ILOTableProps) => {
  const placeholder = auth.loggedIn
    ? 'No data found'
    : 'Connect wallet to get access to limit orders';
  const dispatch = useDispatch();

  const onEditClicked = (order: ILimitOrder) => {
    dispatch(actionShowModal(ModalTypes.limitOrderEdit, { order }));
  };

  const { columns, data, tableColumnsWidth } = useTableData({
    orders,
  });

  const renderCell = (cell: any) => {
    const {
      chain,
      pair,
      src_address,
      dest_address,
      src_decimals,
      dest_decimals,
      amount_in,
      amount_out,
      executed_trades_count,
      trades_count,
    } = cell.row.original;

    const key = `${chain}/${pair}`;
    const ticker = tickers[key];
    const baseAddress = ticker?.flipped
      ? ticker?.quote_address
      : ticker?.base_address;
    const isSourceBase =
      src_address.toLowerCase() === baseAddress?.toLowerCase();
    const srcAmount = new BigNumber(amount_in).div(
      10 ** Number(src_decimals ?? 18)
    );
    const destAmount = new BigNumber(amount_out).div(
      10 ** Number(dest_decimals ?? 18)
    );

    switch (cell.column.id) {
    case 'src':
      return (
        <TokenInfo
          chain={chain}
          address={src_address}
          name={names[src_address]}
          amount={
            srcAmount.isLessThan(new BigNumber(0.0001))
              ? `~${srcAmount.toFixed(6)}`
              : srcAmount.toFixed(4)
          }
        />
      );
    case 'dest':
      return (
        <TokenInfo
          chain={chain}
          address={dest_address}
          name={names[dest_address]}
          amount={
            destAmount.isLessThan(new BigNumber(0.0001))
              ? `~${destAmount.toFixed(6)}`
              : destAmount.toFixed(4)
          }
        />
      );
    case 'actions':
      return (
        <Actions
          order={cell.row.original}
          filterEnabled={key === filter}
          onFilter={onFilter}
        />
      );
    case 'condition':
      return (
        <div className='price_cell'>
          <TickerCell path={key} ticker={tickers[key]} />
          <Text
            size={12}
            onClick={
              cell.row.original.status === enums.LimitOrderStatus.WATCH
                ? () => onEditClicked(cell.row.original)
                : undefined
            }
          >
            {priceDisplayFormatter(cell.row.original)}
          </Text>
        </div>
      );
    case 'side':
      return (
        <Text
          size={12}
          className={`text--${isSourceBase ? 'red-600' : 'green-600'}`}
        >
          {isSourceBase ? 'Sell' : 'Buy'}
        </Text>
      );
    case 'trades':
      return `${executed_trades_count} / ${trades_count}`;
    case 'status':
      return (
        <Badge
          variant={'fill'}
          klass={statusToBadgeKlass(cell.value)}
          text={decorateLimitOrderStatus(cell.value)}
        />
      );
    default:
      return <Text size={12}>{cell.value}</Text>;
    }
  };

  return (
    <DesktopTable
      hiddenColumnsKey={LS_LIMIT_ORDERS_HIDDEN_COLUMNS_KEY}
      dataKey={LS_LIMIT_ORDERS_KEY}
      title='limit_orders'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width}
      columns={columns}
      data={data}
      loading={loading}
      getClassNameForRow={getClassNameForRow}
      renderCell={renderCell}
      placeholderText={placeholder}
    />
  );
};

const mapStateToProps = (state: IStore) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {})(LimitOrdersTable);
